import { useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Twitter, Linkedin, Github } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

export default function Footer({ showInvestor = false }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed. Welcome to the future of power.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="relative overflow-hidden border-t border-white/10 bg-navy-deep px-5 pb-10 pt-20 md:px-8">
      <div className="mx-auto max-w-7xl">
        <h2 className="font-display text-5xl font-black leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-8xl">
          Never plug in <span className="text-revo-green">again.</span>
        </h2>

        <div className="mt-14 grid gap-10 border-t border-white/10 pt-12 md:grid-cols-[1.4fr_1fr_1fr_1.6fr]">
          <div>
            <Link to="/" className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-revo-green">
                <Zap className="h-3.5 w-3.5 text-navy" fill="currentColor" strokeWidth={2.5} />
              </span>
              <span className="font-display text-lg font-extrabold text-white">ChargeRevo</span>
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-revo-ice/70">
              Power is now a subscription. Charge everywhere ChargeRevo is installed — no cables, no pads, no thinking.
            </p>
          </div>

          <div className="flex flex-col gap-3 text-sm">
            <p className="mb-1 font-display font-bold text-white">Product</p>
            {[["Products", "/products"], ["Plans", "/plans"], ["How It Works", "/how-it-works"], ["Technology", "/technology"]].map(([l, to]) => (
              <Link key={to} to={to} className="text-revo-ice/70 transition-colors hover:text-revo-green">{l}</Link>
            ))}
          </div>

          <div className="flex flex-col gap-3 text-sm">
            <p className="mb-1 font-display font-bold text-white">Company</p>
            {[["Business", "/business"], ["About", "/about"], ["FAQ", "/faq"], ["Join / Contact", "/join"]].map(([l, to]) => (
              <Link key={to} to={to} className="text-revo-ice/70 transition-colors hover:text-revo-green">{l}</Link>
            ))}
          </div>

          <div>
            <p className="mb-3 font-display font-bold text-white">Get the launch drop</p>
            <form onSubmit={subscribe} className="flex overflow-hidden rounded-full border border-white/15 bg-white/5 p-1">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="footer-newsletter-input"
                className="w-full bg-transparent px-4 text-sm text-white placeholder:text-revo-ice/40 focus:outline-none"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="footer-newsletter-submit"
                className="shrink-0 rounded-full bg-revo-green px-5 py-2 text-sm font-bold text-navy transition-transform duration-200 hover:scale-105 disabled:opacity-60"
              >
                Subscribe
              </button>
            </form>
            <div className="mt-5 flex gap-3">
              {[Twitter, Linkedin, Github].map((Icon, i) => (
                <a key={i} href="#" aria-label="social" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-revo-ice/70 transition-colors hover:border-revo-green hover:text-revo-green">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-white/10 pt-8 text-xs text-revo-ice/50 md:flex-row md:items-center md:justify-between">
          <p>ChargeRevo. Patent pending. Qi2 and Ki are standards of the Wireless Power Consortium.</p>
          <div className="flex gap-5">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-revo-ice">Privacy</Link>
            <a href="#" className="hover:text-revo-ice">Terms</a>
            <a href="mailto:Requests@ChargeRevo.com" data-testid="contact-email-link" className="hover:text-revo-green">Requests@ChargeRevo.com</a>
            {showInvestor && <a href="mailto:Requests@ChargeRevo.com?subject=Investor%20Relations" data-testid="investor-relations-link" className="hover:text-revo-green">Investor relations</a>}
          </div>
        </div>
      </div>
    </footer>
  );
}
